import React, { useState, useEffect } from 'react';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import adminInboxService from '../../services/adminInboxService';
import PrioritySummaryCard from './components/PrioritySummaryCard';
import InboxFeedPanel from './components/InboxFeedPanel';
import FilterPanel from './components/FilterPanel';

const CentralizedAdminInboxDashboard = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    category: 'all',
    severity: 'all',
    status: 'unread',
    dateRange: '7days',
    searchTerm: ''
  });
  const [selectedItems, setSelectedItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadData();
    
    // Set up real-time subscription
    const subscription = adminInboxService?.subscribeToUpdates((newItem) => {
      setNotifications(prev => [newItem, ...prev]);
      loadSummary();
    });

    return () => {
      adminInboxService?.unsubscribe(subscription);
    };
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [activeTab, filters]);

  const loadData = async () => {
    await Promise.all([loadNotifications(), loadSummary()]);
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await adminInboxService?.getNotifications({
        tab: activeTab,
        ...filters
      });
      setNotifications(data);
      setError('');
    } catch (err) {
      console.error('Error loading notifications:', err);
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await adminInboxService?.getSummary();
      setSummary(data);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleMarkAsRead = async (ids) => {
    try {
      await adminInboxService?.markAsRead(ids);
      await loadData();
      setSelectedItems([]);
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedItems?.length === 0) return;

    try {
      if (action === 'mark_read') {
        await handleMarkAsRead(selectedItems);
      } else if (action === 'archive') {
        await adminInboxService?.archiveItems(selectedItems);
        await loadData();
        setSelectedItems([]);
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
    }
  };

  const handleExport = async () => {
    try {
      await adminInboxService?.exportReport({
        tab: activeTab,
        ...filters
      });
    } catch (err) {
      console.error('Error exporting report:', err);
    }
  };

  const tabs = [
    { id: 'all', label: 'All Notifications', icon: 'Inbox', count: summary?.totalCount || 0 },
    { id: 'payments', label: 'Payments', icon: 'DollarSign', count: summary?.paymentsCount || 0 },
    { id: 'compliance', label: 'Compliance', icon: 'Shield', count: summary?.complianceCount || 0 },
    { id: 'support', label: 'Support', icon: 'MessageSquare', count: summary?.supportCount || 0 },
    { id: 'billing', label: 'Billing', icon: 'FileText', count: summary?.billingCount || 0 }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Header />
      
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Icon name="Inbox" size={28} className="text-blue-600" />
                Centralized Admin Inbox
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage all client payments, compliance alerts, support requests, and billing notifications
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={handleExport}
              >
                Export Report
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="RefreshCw"
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Priority Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <PrioritySummaryCard
            title="Urgent Payments"
            count={summary?.urgentPayments || 0}
            icon="AlertCircle"
            color="red"
            description="Failed transactions & overdue"
          />
          <PrioritySummaryCard
            title="Critical Compliance"
            count={summary?.criticalCompliance || 0}
            icon="ShieldAlert"
            color="amber"
            description="Employment Act violations"
          />
          <PrioritySummaryCard
            title="Pending Support"
            count={summary?.pendingSupport || 0}
            icon="Clock"
            color="blue"
            description="Tickets requiring attention"
          />
          <PrioritySummaryCard
            title="Billing Alerts"
            count={summary?.billingAlerts || 0}
            icon="Bell"
            color="green"
            description="Notifications requiring action"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-3">
            <FilterPanel
              filters={filters}
              onFiltersChange={setFilters}
            />
          </div>

          {/* Main Inbox Panel */}
          <div className="lg:col-span-9">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
              {/* Tabs */}
              <div className="border-b border-gray-200 dark:border-slate-700">
                <div className="flex overflow-x-auto">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab?.id
                          ? 'border-blue-600 text-blue-600 dark:text-blue-400' :'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      {tab?.label}
                      {tab?.count > 0 && (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          activeTab === tab?.id
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' :'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                        }`}>
                          {tab?.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedItems?.length > 0 && (
                <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-blue-700 dark:text-blue-300">
                      {selectedItems?.length} item(s) selected
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('mark_read')}
                      >
                        Mark as Read
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleBulkAction('archive')}
                      >
                        Archive
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedItems([])}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Feed */}
              <InboxFeedPanel
                notifications={notifications}
                loading={loading}
                error={error}
                selectedItems={selectedItems}
                onSelectionChange={setSelectedItems}
                onRefresh={loadNotifications}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CentralizedAdminInboxDashboard;